import type { FolderPath } from '@lib/library/folderPath';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import {
  AddMediaModal,
  AddMediaModalRef,
} from '@pages/Libraries/components/AddMediaModal/AddMediaModal';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { ReactNode, useEffect, useRef } from 'react';
import css from './UploadButton.module.scss';

export type LibraryAndFolder = {
  libraryId: string;
  /**
   * The folder of that library the media is filed in; its root when absent. Honoured only while
   * the modal's destination is still this library — pick another one and it files in that
   * library's root.
   */
  folderPath?: FolderPath;
};

export const UploadButton = ({
  variant = 'secondary',
  text = 'Add media',
  destination,
  disabled = false,
  tooltipTitle = 'Upload or add media links',
  onRegisterClose,
  onDisabledClick,
  compact = false,
  renderTrigger,
}: {
  variant?: 'secondary' | 'primary';
  text?: string;
  destination?: LibraryAndFolder;
  disabled?: boolean;
  tooltipTitle?: string;
  onDisabledClick?: () => void;
  /** When provided (e.g. in library flow), register this modal's close so it can be closed when the page's file-upload confirm runs */
  onRegisterClose?: (getClose: () => void) => () => void;
  /**
   * Abbreviate to a bare `+` in a 40px circle — drops `text`. Set from the row's width by the library
   * header (`RIGHT_LABELS_BELOW` in `LibraryContent`).
   *
   * Keeps the FILLED variant rather than going frosted like the icon buttons beside it: this is the
   * lead action, and the verb rail's frosted circles sit a few pixels away.
   *
   * `text` stays the accessible name, so this abbreviates the *rendering* and not the label:
   * whatever a caller passes ("Add media", or something surface-specific) is what a screen reader
   * and the tooltip still say.
   */
  compact?: boolean;
  /** The button itself, in a caller's look. The tooltip, the modal and the tracking stay. */
  renderTrigger?: (props: { onClick: () => void; disabled: boolean }) => ReactNode;
}) => {
  const addMediaModalRef = useRef<AddMediaModalRef>(null);

  useEffect(() => {
    // Register the close callback so the library flow can dismiss the add-media
    // modal (AddMediaModal adapts to a bottom sheet on mobile itself).
    const close = () => addMediaModalRef.current?.close();
    const unregister = onRegisterClose?.(close);
    return () => unregister?.();
  }, [onRegisterClose]);

  const handleClick = () => {
    trackingStore.addVideosButtonClicked({
      libraryId: destination?.libraryId ?? 'unknown',
    });
    addMediaModalRef.current?.open();
  };

  return (
    <>
      <Tooltip title={tooltipTitle} placement="top" arrow>
        <span
          onClick={disabled && onDisabledClick ? onDisabledClick : undefined}
          className={disabled && onDisabledClick ? css.disabledClickable : undefined}
        >
          {renderTrigger ? (
            renderTrigger({ onClick: handleClick, disabled })
          ) : (
            <ActionButton
              variant={variant}
              className={classNames(css.uploadButton, compact && css.compact)}
              onClick={handleClick}
              disabled={disabled}
              aria-label={compact ? text : undefined}
            >
              {compact ? <AddRoundedIcon className={css.compactIcon} /> : text}
            </ActionButton>
          )}
        </span>
      </Tooltip>
      <AddMediaModal ref={addMediaModalRef} destination={destination} />
    </>
  );
};
